import { useState, useEffect } from 'preact/hooks';
import Modal from './shared/Modal.jsx';
import Button from './shared/Button.jsx';
import { formatEuros, formatDate, INTERVAL_LABEL } from '../lib/format.js';

// Alles wat een klant bij zijn aanmelding opgaf, en het bewijs van zijn akkoord.
// Dit is wat Nick bekijkt voordat hij goedkeurt. Een vereniging (Vereniging4U)
// of een BV (Pand4U): die laatste heeft een omvang en diensten in plaats van een
// soort, afdelingen en extra's.

const MAIL_LABEL = {
  pending: 'nog niet verstuurd',
  sent: 'verstuurd',
  skipped: 'niet verstuurd: geen SMTP ingesteld',
  failed: 'mislukt'
};

const PER_LABEL = { once: 'eenmalig', month: 'per maand', year: 'per jaar' };

// Een aanmelding hangt aan een bestaande debiteur als het factuuradres al
// bekend was. Dat is niet hetzelfde als dezelfde vereniging: wie dat adres kent,
// kan zich zo namens een klant aanmelden. Wijken naam of KvK af, dan hoort Nick
// dat te zien vóór hij goedkeurt.
export function differsFromDebtor(request) {
  const customer = request.order.customer;
  return customer.kvkNr !== request.debtorKvkNr ||
    customer.name.trim().toLowerCase() !== request.debtorName.trim().toLowerCase();
}

export default function SignupRequestModal({ request, onClose, onRetry, retrying, onResend, sending, onLinked }) {
  if (!request) return null;
  const { order, consent } = request;
  const customer = order.customer;
  const ceiling = order.plan.maxUsers == null ? 'onbeperkt' : `t/m ${order.plan.maxUsers} gebruikers`;
  // Pand4U herkent een debiteur aan het KvK-nummer, Vereniging4U aan het factuuradres
  const bv = order.product.slug === 'pand4u';
  const customerNoun = bv ? 'BV' : 'vereniging';

  return (
    <Modal isOpen title={`Aanmelding ${customer.name}`} size="wide" onClose={onClose}>
      {request.status === 'pending' && (request.suggestion || request.linkMode === 'existing') && (
        <LinkSection request={request} onLinked={onLinked} />
      )}
      {request.status === 'pending' && !request.suggestion && request.linkMode === 'new' && (
        <details class="link-manual">
          <summary>Toch van een bestaande klant? Koppelen…</summary>
          <LinkSection request={request} onLinked={onLinked} />
        </details>
      )}
      {request.linkMode === 'existing' && request.status === 'approved' && (
        <div class="notice notice-info">
          Gekoppeld aan de bestaande klant "{request.debtorName}". Het akkoord hoort bij diens lopende abonnement;
          er is niets aangemaakt in het product en niemand uitgenodigd.
        </div>
      )}
      {!request.suggestion && request.linkMode === 'new' && differsFromDebtor(request) && (
        <div class="notice notice-warning">
          Deze aanmelding hangt aan de bestaande debiteur "{request.debtorName}" (KvK {request.debtorKvkNr}),
          omdat {bv ? 'het KvK-nummer' : 'het factuuradres'} gelijk is. De ingevulde naam{bv ? '' : ' of het KvK-nummer'} wijkt daarvan af.
          Controleer of het dezelfde {customerNoun} is voordat je goedkeurt.
        </div>
      )}
      <dl class="detail-list">
        <dt>Pakket</dt>
        <dd>{order.product.name} {order.plan.name} ({ceiling}), {formatEuros(order.plan.priceCents)} {INTERVAL_LABEL[order.plan.billingInterval]} excl. btw</dd>
        {order.departments && (
          <>
            <dt>Afdelingen</dt>
            <dd>
              {order.departments.names.join(', ')}
              {order.departments.extraCount > 0 && (
                <div>Toeslag: {order.departments.extraCount} × {formatEuros(order.departments.extraUnitCents)} {INTERVAL_LABEL[order.plan.billingInterval]} excl. btw</div>
              )}
              {request.linkMode === 'existing' && (
                <div class="muted small">Bestaande klant: koppelen verandert het abonnement niet. Het aantal afdelingen en de toeslag komen bij de volgende ophaalronde uit de app.</div>
              )}
            </dd>
          </>
        )}
        <dt>Ingangsdatum</dt>
        <dd>{formatDate(request.startDate)}{request.subscriptionStartedOn && request.subscriptionStartedOn !== request.startDate
          ? <span class="muted"> ({request.linkMode === 'existing' ? 'abonnement loopt sinds' : 'facturatie vanaf'} {formatDate(request.subscriptionStartedOn)})</span> : null}</dd>
        {order.size ? (
          <>
            <dt>Omvang</dt>
            <dd>{order.size.objects} objecten, {order.size.units} eenheden</dd>
            <dt>Diensten</dt>
            <dd>
              {order.services.length === 0 ? 'geen' : order.services.map(s =>
                <div key={s.key}>{s.label}: {formatEuros(s.monthlyCents)} per maand</div>)}
              <div class="muted small">Aan- en uitzetten bij het abonnement, onder Abonnementen.</div>
              {order.services.length > 0 && (request.linkMode === 'existing' || request.suggestion) && (
                <div class="muted small">Bestaande klant: koppelen zet deze diensten niet vanzelf aan. Wil de klant ze, zet ze dan aan bij het lopende abonnement.</div>
              )}
            </dd>
            <dt>Hulp bij overstappen</dt>
            <dd>{order.migrationHelp ? 'ja, gratis' : 'nee'}</dd>
          </>
        ) : (
          <>
            <dt>Soort, taal</dt>
            <dd>{request.clubType}, {request.language}</dd>
            <dt>Extra's</dt>
            <dd>{order.extras.length === 0 ? 'geen' : order.extras.map(e =>
              <div key={e.key}>{e.label}: {formatEuros(e.priceCents)} {PER_LABEL[e.per]}</div>)}</dd>
          </>
        )}
      </dl>

      <h4 class="detail-heading">{bv ? 'BV' : 'Vereniging'} zoals ingevuld</h4>
      <dl class="detail-list">
        <dt>Statutaire naam</dt><dd>{customer.name}</dd>
        <dt>Adres</dt><dd>{customer.address.street} {customer.address.houseNr}, {customer.address.postcode} {customer.address.city}</dd>
        <dt>KvK</dt><dd>{customer.kvkNr}</dd>
        <dt>Btw</dt><dd>{customer.btwNr || <span class="muted">geen</span>}</dd>
        <dt>Factuur-e-mail</dt><dd>{customer.email}</dd>
        <dt>T.a.v.</dt><dd>{customer.invoiceAttention || <span class="muted">—</span>}</dd>
        <dt>Referentie</dt><dd>{customer.invoiceReference || <span class="muted">—</span>}</dd>
      </dl>

      <h4 class="detail-heading">Personen</h4>
      <dl class="detail-list">
        <dt>Ondertekenaar</dt><dd>{request.signer.name}, {request.signer.role} ({request.signer.email}), verklaart bevoegd te zijn</dd>
        <dt>Eerste beheerder</dt><dd>{request.admin.name} ({request.admin.email})</dd>
      </dl>

      <h4 class="detail-heading">Akkoord</h4>
      <dl class="detail-list">
        <dt>Documenten</dt><dd>Algemene voorwaarden v{consent.termsVersion}, verwerkersovereenkomst v{consent.dpaVersion}</dd>
        <dt>Moment</dt><dd>{new Date(consent.at).toLocaleString('nl-NL', { timeZone: 'Europe/Amsterdam' })}</dd>
        <dt>IP-adres</dt><dd>{consent.ip || <span class="muted">onbekend</span>}</dd>
        <dt>Browser</dt><dd class="muted detail-small">{consent.userAgent || 'onbekend'}</dd>
        <dt>Vingerafdruk</dt>
        <dd class="muted detail-small">
          {consent.termsSha256
            ? <>voorwaarden {consent.termsSha256.slice(0, 12)}…, verwerkersovereenkomst {consent.dpaSha256.slice(0, 12)}…</>
            : 'niet vastgelegd (aanmelding van vóór 19 oktober 2026)'}
        </dd>
        <dt>PDF's</dt>
        <dd>
          <a href={`/api/signup-requests/${request.id}/order-form.pdf`} target="_blank" rel="noopener">bestelformulier</a>
          {' · '}<a href={`/api/signup-requests/${request.id}/terms.pdf`} target="_blank" rel="noopener">voorwaarden {consent.termsVersion}</a>
          {' · '}<a href={`/api/signup-requests/${request.id}/dpa.pdf`} target="_blank" rel="noopener">verwerkersovereenkomst {consent.dpaVersion}</a>
        </dd>
      </dl>

      <h4 class="detail-heading">Mails aan de {customerNoun}</h4>
      <dl class="detail-list">
        <dt>Bevestiging</dt>
        <dd>
          <MailStatus mail={request.mail} />
          <ResendButton request={request} kind="confirmation" onResend={onResend} sending={sending} />
        </dd>
        <dt>Overeenkomst rond</dt>
        <dd>
          {request.status === 'pending'
            ? <span class="muted">gaat uit bij goedkeuren of koppelen</span>
            : request.status !== 'approved'
              ? <span class="muted">niet van toepassing: de aanmelding is afgewezen</span>
              : <>
                <MailStatus mail={request.agreementMail} />
                <ResendButton request={request} kind="agreement" onResend={onResend} sending={sending} />
              </>}
        </dd>
      </dl>

      <h4 class="detail-heading">Inrichting in {order.product.name}</h4>
      <ProvisionStatus request={request} onRetry={onRetry} retrying={retrying} />
    </Modal>
  );
}

function MailStatus({ mail }) {
  return (
    <>
      {MAIL_LABEL[mail.status]}
      {mail.status === 'sent' && mail.at && <span class="muted">, {formatDate(mail.at)}</span>}
      {mail.error && <div class="form-error">{mail.error}</div>}
    </>
  );
}

function ResendButton({ request, kind, onResend, sending }) {
  if (!onResend) return null;
  const busy = sending === `${request.id}:${kind}`;
  const label = request[kind === 'agreement' ? 'agreementMail' : 'mail'].status === 'sent' ? 'Opnieuw versturen' : 'Versturen';
  return (
    <div style="margin-top: 6px">
      <Button size="sm" variant="ghost" onClick={() => onResend(request, kind)} disabled={busy}>{busy ? 'Bezig…' : label}</Button>
    </div>
  );
}

export function ProvisionStatus({ request, onRetry, retrying }) {
  const { provision } = request;
  if (request.linkMode === 'existing' || provision.status === 'not_needed') {
    return <p class="muted">Niet nodig: de club bestaat al{request.accountRef ? <> als <code>{request.accountRef}</code></> : null}.</p>;
  }
  if (request.status === 'pending') {
    return <p class="muted">Gebeurt bij goedkeuren.</p>;
  }
  if (request.status === 'rejected') {
    return <p class="muted">Niet van toepassing: de aanmelding is afgewezen.</p>;
  }
  if (provision.status === 'done') {
    return (
      <p>
        {provision.created ? 'Aangemaakt' : 'Bestond al'} als <code>{request.accountRef}</code>
        <span class="muted">, {formatDate(provision.at)}</span>
      </p>
    );
  }
  return (
    <div class="notice notice-error">
      {provision.status === 'failed'
        ? <>Mislukt na {provision.attempts} {provision.attempts === 1 ? 'poging' : 'pogingen'}: {provision.error}</>
        : 'Nog niet geprobeerd.'}
      <div style="margin-top: 8px; display: flex; gap: 6px; flex-wrap: wrap">
        <Button size="sm" onClick={() => onRetry(request)} disabled={retrying}>
          {retrying ? 'Bezig…' : 'Opnieuw proberen'}
        </Button>
        {/* Gestopt op een sleutel van een bestaande klant: alleen doorgaan als het
            echt een andere vereniging is. */}
        {provision.status === 'failed' && !request.newClubConfirmed && provision.error?.includes('hoort al bij') && (
          <Button size="sm" variant="ghost" disabled={retrying} onClick={() => {
            if (confirm('Bevestig je dat dit een andere vereniging is? Er komt dan een tweede club in het product, met een sleutel met volgnummer.')) {
              onRetry(request, { confirmNewClub: true });
            }
          }}>Toch een nieuwe club</Button>
        )}
        {/* Gestopt omdat het product al een club met deze naam heeft, of misschien
            heeft (een eerdere poging zonder antwoord): alleen een nieuwe club als het
            echt een andere vereniging is (bijv. dezelfde naam in een andere plaats).
            Geldt voor deze ene poging, en alleen zonder vaste sleutel: met een sleutel
            op de koppeling is er geen volgnummer. */}
        {provision.status === 'failed' && !request.accountRef && provision.error?.includes('bewust als nieuwe club aan') && (
          <Button size="sm" variant="ghost" disabled={retrying} onClick={() => {
            if (confirm('Bevestig je dat dit een andere vereniging is dan de club met dezelfde naam? Er komt dan een tweede club in het product, met een sleutel met volgnummer.')) {
              onRetry(request, { forceNewClub: true });
            }
          }}>Toch als nieuwe club aanmaken</Button>
        )}
      </div>
    </div>
  );
}

// Koppelen aan een bestaande klant: kiezen welke, zien wat er afwijkt van wat de
// vereniging invulde, en dan bevestigen. Er verandert niets aan het abonnement
// of de klantgegevens; wil Nick de keuze van het formulier overnemen, dan doet
// hij dat daarna zelf.
function LinkSection({ request, onLinked }) {
  const [candidates, setCandidates] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/signup-requests/${request.id}/link-candidates`, { credentials: 'same-origin' })
      .then(res => res.ok ? res.json() : Promise.reject(new Error('load_failed')))
      .then(data => {
        if (cancelled) return;
        setCandidates(data.candidates);
        const preferred = data.candidates.find(c => c.debtorId === (request.suggestion?.debtorId ?? request.debtorId)) ?? data.candidates[0];
        setChosen(preferred?.debtorId ?? null);
      })
      .catch(() => !cancelled && setError('De klanten konden niet geladen worden.'));
    return () => { cancelled = true; };
  }, [request.id]);

  async function link() {
    const target = candidates.find(c => c.debtorId === chosen);
    if (!target) return;
    if (!confirm(`Deze aanmelding koppelen aan "${target.name}"? Het akkoord komt bij het lopende abonnement te staan, er wordt niets aangemaakt in het product, en de vereniging krijgt de mail "overeenkomst rond".`)) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/signup-requests/${request.id}/link`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ debtor_id: target.debtorId })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error === 'customer_not_linkable'
          ? 'Deze klant heeft geen lopend abonnement met een sleutel voor dit product.'
          : 'Koppelen is mislukt.');
        return;
      }
      onLinked?.(data.request, data.agreementMail);
    } finally {
      setBusy(false);
    }
  }

  const target = candidates?.find(c => c.debtorId === chosen);
  const suggestionNotLinkable = request.suggestion && candidates && !candidates.some(c => c.debtorId === request.suggestion.debtorId);

  return (
    <div class="link-section">
      <h4 class="detail-heading">Bestaande klant</h4>
      {request.suggestion && (
        <p>Lijkt op <strong>{request.suggestion.debtorName}</strong> ({request.suggestion.reason}).</p>
      )}
      {suggestionNotLinkable && (
        <p class="form-warning-inline">Die klant heeft geen lopend abonnement voor dit product, dus koppelen kan niet. Is het een nieuwe club, keur dan goed als nieuwe club.</p>
      )}
      {error && <div class="form-error">{error}</div>}
      {candidates == null && !error && <p class="muted">Klanten laden…</p>}
      {candidates?.length === 0 && <p class="muted">Er zijn geen klanten met een lopend abonnement voor dit product.</p>}
      {candidates?.length > 0 && (
        <>
          <label class="link-choose">
            <span class="muted">Koppelen aan</span>
            <select value={chosen ?? ''} onChange={e => setChosen(Number(e.currentTarget.value))}>
              {candidates.map(c => (
                <option key={c.debtorId} value={c.debtorId}>{c.name} ({c.accountRef}){c.suggested ? ' — voorgesteld' : ''}</option>
              ))}
            </select>
          </label>
          {target && (
            <table class="data-table diff-table">
              <thead><tr><th /><th>Ingevuld</th><th>Nu bij de klant</th></tr></thead>
              <tbody>
                {target.differences.map(row => (
                  <tr key={row.label} class={row.differs ? 'diff-row' : ''}>
                    <td class="muted">{row.label}</td>
                    <td>{row.filledIn}</td>
                    <td>{row.current}{row.differs && <span class="form-warning-inline"> · wijkt af</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p class="muted detail-small">
            Koppelen verandert niets aan het abonnement, het pakket, de prijs of de klantgegevens.
            Wil je de keuze van het formulier overnemen, pas dat daarna aan bij Abonnementen of met Bewerken.
            De opgegeven beheerder ({request.admin.name}) wordt niet uitgenodigd: de club bestaat al.
          </p>
          <Button size="sm" onClick={link} disabled={busy || !target}>{busy ? 'Bezig…' : `Koppelen aan ${target?.name ?? '…'}`}</Button>
        </>
      )}
    </div>
  );
}
